import {
  PointOfSalesResponse,
  type OfflinePointOfSalesResponse,
  type OnlinePointOfSalesResponse,
  getMallTitlesMap,
  PointOfSalesType,
} from '~domains/partner-application/entities/application';
import { useMalls } from '~domains/partner-application/entities/mall';

import { InfoCard } from '~shared/ui/info-card';
import { InfoField } from '~shared/ui/info-field';

import { Button } from '@breezeventures/ui-kit/button';
import { Typography } from '@breezeventures/ui-kit/typography';

import { AddressReviewItems } from './address-review-items';
import { DocumentReviewItem } from './document-review-item';

const storeTypeToTitle: Record<PointOfSalesType, string> = {
  ONLINE: 'Online store',
  OFFLINE: 'Physical store',
};

const StoreDetails = ({
  store,
}: {
  store: OfflinePointOfSalesResponse | OnlinePointOfSalesResponse;
}) => {
  const { data: malls = [] } = useMalls();
  const isOfflineStore = store.type === 'OFFLINE';

  const mallTitlesMap = getMallTitlesMap(malls);

  return (
    <div className="grid grid-cols-2 gap-4">
      <InfoField title="Type" value={storeTypeToTitle[store.type]} />
      <InfoField title="Store name" value={store.name} />
      <InfoField title="Store e-mail address" value={store.email} />
      <InfoField title="Store contact number" value={store.contactNumber} />

      {isOfflineStore && (
        <InfoField
          title="Tax Identification number (TIN)"
          value={store.tinCode}
        />
      )}

      <InfoField
        title="Avg sales volume per month (PHP)"
        value={store.avgMonthlySales}
      />
      <InfoField
        title="Website or social media page (if any)"
        value={store.websiteURL}
      />

      {isOfflineStore && (
        <>
          <InfoField
            title="Store size (sq.m.)"
            value={store.areaSqm}
            hideIfEmpty
          />
          <InfoField title="Number of floors" value={store.numberOfFloors} />
          <InfoField
            title="The store have CCTV cameras"
            value={store.hasCCTV ? 'Yes' : 'No'}
          />
          <InfoField
            title="How many days are recordings saved?"
            value={store.dayOfRecording}
            hideIfEmpty
          />

          <AddressReviewItems address={store.address} />

          <InfoField
            title="What mall is your store located in?"
            value={mallTitlesMap[store.mallId ?? '']}
          />

          <InfoField
            title="Mall name (Helps customers find you)"
            value={store.mallNameOther}
            hideIfEmpty
          />

          <div className="col-span-full flex flex-col gap-3">
            {store.photos.map((photo) => (
              <DocumentReviewItem
                key={photo.documentId}
                title="Store photo"
                document={photo}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const StoresReview = ({
  pointsOfSales,
  onEdit,
}: {
  pointsOfSales: PointOfSalesResponse[] | undefined;
  onEdit?: () => void;
}) => {
  if (!pointsOfSales?.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 pt-3">
      <div className="flex items-center justify-between gap-2">
        <Typography.Title size="2xs" tag="div">
          Stores
        </Typography.Title>
        {onEdit && (
          <Button
            onClick={onEdit}
            type="button"
            size="s"
            variant="secondary"
            aria-label="Edit Stores"
          >
            Edit
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-3">
        {pointsOfSales.map((store) => (
          <InfoCard
            key={store.uiId}
            header={store.name}
            subheader={
              store.type === 'OFFLINE'
                ? store.address.streetAndHouse
                : store.websiteURL
            }
            expandable
          >
            <StoreDetails store={store} />
          </InfoCard>
        ))}
      </div>
    </div>
  );
};
